/**
 * b18-profile-1: PvP event strip for the Profile trophy slot.
 *
 * Renders the top PvP/league events from the existing /api/events/feed
 * endpoint, which already scores events by isPvpTrainer + leagueFoci.
 * Free trainers see the top 1 event plus a locked teaser for the rest.
 * Pro trainers see all 3 events.
 *
 * Styling mirrors EventForYou.tsx but tighter for Profile density.
 * No em-dashes. No hardcoded hex colours. No emojis.
 */
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Swords, Lock } from "lucide-react";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import { eventCountdownDate } from "@/lib/calendarLinks";
import { EventTypePill } from "@/components/EventTypePill";
import { InfoBlock } from "@/components/InfoBlock";
import { leagueFocusLabel } from "@/lib/userPriorities";
import type { LeagueFocus } from "@/lib/userPriorities";
import type { GameEvent } from "@/lib/data";

// ---- types ----------------------------------------------------------------

interface FeedEvent {
  id: string;
  name: string;
  heading?: string;
  category: GameEvent["category"];
  start: string;
  end: string;
  image?: string;
}

interface FeedRow {
  eventId: string;
  rank: number;
  score: number;
  reasons: unknown[];
  llmSummary: string | null;
  llmModel: string | null;
  llmGeneratedAt: string | null;
  event: FeedEvent | undefined;
}

interface FeedResponse {
  feed: FeedRow[];
  pro: boolean;
}

// ---- helpers ---------------------------------------------------------------

const PVP_CATEGORIES = new Set(["pvp", "league"]);

function isPvpRow(row: FeedRow): boolean {
  return !!row.event && PVP_CATEGORIES.has(row.event.category);
}

function eventCountdown(start: string, end: string | undefined, now: Date = new Date()): string | null {
  const s = eventCountdownDate(start);
  const e = end ? eventCountdownDate(end) : null;
  const t = now.getTime();
  if (Number.isNaN(s.getTime())) return null;
  if (e && !Number.isNaN(e.getTime()) && e.getTime() < t) return "Wrapped up";
  if (s.getTime() > t) {
    const mins = Math.round((s.getTime() - t) / 60000);
    if (mins < 60) return `Starts in ${Math.max(1, mins)}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Starts in ${hrs}h ${Math.floor(mins % 60)}m`;
    return `Starts in ${Math.round(hrs / 24)}d`;
  }
  if (!e || Number.isNaN(e.getTime())) return "Active now";
  const remMs = e.getTime() - t;
  const hrs = Math.floor(remMs / 36e5);
  const mins = Math.floor((remMs % 36e5) / 60000);
  if (hrs < 1) return `${Math.max(1, mins)}m left`;
  if (hrs < 48) return `${hrs}h ${mins}m left`;
  return `${Math.round(hrs / 24)}d left`;
}

function buildHeading(leagueFoci: LeagueFocus[]): string {
  const active = leagueFoci.filter((l) => l !== "none");
  if (active.length >= 1 && active.length <= 2) {
    return `Your ${active.map((l) => leagueFocusLabel(l)).join(" + ")} events`;
  }
  return "PvP events for you";
}

// ---- components -----------------------------------------------------------

function SkeletonCard() {
  return (
    <article
      className="rounded-xl p-3"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
      }}
      aria-hidden
    >
      <div className="flex items-start gap-2.5">
        <div
          className="shrink-0 w-9 h-9 rounded-lg"
          style={{ background: "var(--surface-overlay)" }}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div
            className="h-2.5 w-32 rounded"
            style={{ background: "var(--surface-overlay)" }}
          />
          <div
            className="h-2 w-20 rounded"
            style={{ background: "var(--surface-overlay)" }}
          />
        </div>
      </div>
    </article>
  );
}

function EventCard({ row, onPlan }: { row: FeedRow; onPlan: () => void }) {
  const ev = row.event!;
  const countdown = eventCountdown(ev.start, ev.end);

  return (
    <article
      data-testid={`pvp-event-card-${row.eventId}`}
      className="rounded-xl p-3"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <div className="flex items-start gap-2.5">
        {ev.image ? (
          <img
            src={ev.image}
            alt=""
            aria-hidden
            className="shrink-0 w-9 h-9 rounded-lg object-cover"
          />
        ) : (
          <span
            className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: "var(--surface-overlay)" }}
            aria-hidden
          >
            <Swords
              className="w-4 h-4"
              style={{ color: "var(--brand-green)" }}
            />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold leading-tight"
            style={{ color: "var(--text-primary)" }}>
            {ev.name}
          </p>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            <EventTypePill category={ev.category} />
            {countdown && (
              <span
                className="font-mono text-[10px]"
                style={{ color: "var(--text-secondary)" }}
              >
                {countdown}
              </span>
            )}
          </div>
          <button
            type="button"
            data-testid={`pvp-event-plan-${row.eventId}`}
            onClick={onPlan}
            className="mt-2 rounded-full px-3 py-1 text-[11px] font-bold"
            style={{
              background: "color-mix(in srgb, var(--brand-green) 14%, transparent)",
              border: "1px solid color-mix(in srgb, var(--brand-green) 38%, transparent)",
              color: "var(--brand-green)",
            }}
          >
            Plan it
          </button>
        </div>
      </div>
    </article>
  );
}

function LockedTeaserTile({
  remaining,
  leagueFoci,
  onUnlock,
}: {
  remaining: number;
  leagueFoci: LeagueFocus[];
  onUnlock: () => void;
}) {
  const active = leagueFoci.filter((l) => l !== "none");
  const leagueLabel =
    active.length > 0 && active.length <= 2
      ? active.map((l) => leagueFocusLabel(l)).join(" + ")
      : "your leagues";

  return (
    <button
      type="button"
      data-testid="pvp-event-locked-teaser"
      onClick={onUnlock}
      className="w-full text-left rounded-xl p-3 flex items-center gap-2.5"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
        opacity: 0.7,
      }}
    >
      <span
        className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ background: "var(--surface-overlay)" }}
        aria-hidden
      >
        <Lock
          className="w-4 h-4"
          style={{ color: "var(--text-secondary)" }}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className="block text-[12px] font-semibold"
          style={{ color: "var(--text-primary)" }}
        >
          {remaining} more PvP {remaining === 1 ? "event" : "events"} tailored for your {leagueLabel} leagues
        </span>
        <span
          className="block text-[11px] mt-0.5"
          style={{ color: "var(--accent-fill)" }}
        >
          Pro unlocks the full Profile feed
        </span>
      </span>
    </button>
  );
}

// ---- main component --------------------------------------------------------

export interface PvpEventStripProps {
  leagueFoci: LeagueFocus[];
}

export function PvpEventStrip({ leagueFoci }: PvpEventStripProps) {
  const { tier, openPaywall } = useSession();
  const isPro = hasProAccess(tier);
  const [, setLocation] = useLocation();

  const feedQ = useQuery<FeedResponse>({
    queryKey: ["/api/events/feed"],
    staleTime: 60_000,
  });

  // Loading state
  if (feedQ.isLoading) {
    return (
      <InfoBlock icon={Swords} title="PvP events" data-testid="pvp-event-strip-loading">
        <div className="space-y-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </InfoBlock>
    );
  }

  // Error: silent fail -- Profile must not break on feed errors
  if (feedQ.isError) {
    return null;
  }

  const pvpRows = (feedQ.data?.feed ?? [])
    .filter(isPvpRow)
    .slice(0, 3);

  const heading = buildHeading(leagueFoci);

  // Empty state
  if (pvpRows.length === 0) {
    return (
      <InfoBlock icon={Swords} title={heading} data-testid="pvp-event-strip-empty">
        <p className="text-[12px] text-[var(--text-secondary)]">
          No PvP events on the radar right now. We are watching the calendar.
        </p>
      </InfoBlock>
    );
  }

  const visibleRows = isPro ? pvpRows : pvpRows.slice(0, 1);
  const lockedCount = isPro ? 0 : pvpRows.length - 1;

  return (
    <InfoBlock icon={Swords} title={heading} data-testid="pvp-event-strip">
      <div className="space-y-2">
        {visibleRows.map((row) => (
          <EventCard
            key={row.eventId}
            row={row}
            onPlan={() => setLocation("/calendar")}
          />
        ))}

        {!isPro && lockedCount > 0 && (
          <LockedTeaserTile
            remaining={lockedCount}
            leagueFoci={leagueFoci}
            onUnlock={() =>
              openPaywall("pvp-events", "See PvP events tailored to your leagues (Pro)")
            }
          />
        )}
      </div>
    </InfoBlock>
  );
}
